import React, { useEffect, useMemo, useState } from 'react';
import { getRankerFixList } from '../../lib/api/ranker-api';
import { getFavoriteGroupComplexes, getFavoriteGroups } from '../../lib/api/favorite-api';
import './RankerComplexSearchPopup.css';

const toCode = (value) => String(value || '').trim();
const PAGE_SIZE = 10;

// 타입/면적 문자열에서 평 라벨 추출 (비교 추가 시 표기와 동일 규칙: 숫자 ≤40 = 평, 그 외 = ㎡)
const parsePyeongLabel = (str) => {
  const nums = (String(str || '').match(/\d+(?:\.\d+)?/g) || [])
    .map(Number).filter((n) => Number.isFinite(n) && n > 0)
    .map((n) => (n <= 40 ? n * 3.305785 : n)); // ㎡ 기준으로 통일
  if (nums.length === 0) return '';
  const ps = nums.map((m2) => Math.round(m2 / 3.305785));
  const pMin = Math.min(...ps);
  const pMax = Math.max(...ps);
  return pMin === pMax ? `${pMin}평` : `${pMin}~${pMax}평`;
};

function RankerComplexSearchPopup({
  open = false,
  onClose,
  onSelect,
  excludeCodes = [],
  title = '비교 단지 추가',
  showFavoriteGroups = true,
}) {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searched, setSearched] = useState(false); // 검색 수행 여부(최초에는 결과 없음)
  const [activeTab, setActiveTab] = useState('search'); // 'search' | 'favorite'

  const [favoriteGroups, setFavoriteGroups] = useState([]);
  const [selectedFavoriteGroupId, setSelectedFavoriteGroupId] = useState(0);
  const [favoriteRows, setFavoriteRows] = useState([]);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [favoriteError, setFavoriteError] = useState('');

  const excludeSet = useMemo(
    () => new Set((Array.isArray(excludeCodes) ? excludeCodes : []).map((code) => toCode(code))),
    [excludeCodes]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setKeyword('');
      setRows([]);
      setError('');
      setLoading(false);
      setPageNumber(1);
      setTotalCount(0);
      setSearched(false);
      setActiveTab('search');
      setFavoriteGroups([]);
      setSelectedFavoriteGroupId(0);
      setFavoriteRows([]);
      setFavoriteLoading(false);
      setFavoriteError('');
    }
  }, [open]);

  const loadPage = async (nextPage = 1) => {
    const q = String(keyword || '').trim();
    // 단지명 미입력 시 조회하지 않음(최초/빈 검색 → 결과 없음)
    if (!q) {
      setRows([]);
      setTotalCount(0);
      setPageNumber(1);
      setSearched(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const response = await getRankerFixList({
        keyword: q,
        pageNumber: nextPage,
        pageSize: PAGE_SIZE,
      });
      const items = Array.isArray(response?.items) ? response.items : [];
      setRows(items);
      setTotalCount(Number(response?.totalCount || 0));
      setPageNumber(nextPage);
    } catch (e) {
      setRows([]);
      setTotalCount(0);
      setError(e?.message || '단지 검색 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !showFavoriteGroups) return undefined;
    let alive = true;

    const loadGroups = async () => {
      setFavoriteLoading(true);
      setFavoriteError('');
      try {
        const response = await getFavoriteGroups();
        if (!alive) return;
        const groups = Array.isArray(response?.items) ? response.items : [];
        setFavoriteGroups(groups);
        setSelectedFavoriteGroupId(Number(groups?.[0]?.groupId || 0));
      } catch (e) {
        if (!alive) return;
        setFavoriteGroups([]);
        setSelectedFavoriteGroupId(0);
        setFavoriteRows([]);
        setFavoriteError(e?.message || '즐겨찾기 그룹을 불러오지 못했습니다.');
      } finally {
        if (alive) setFavoriteLoading(false);
      }
    };

    loadGroups();
    return () => {
      alive = false;
    };
  }, [open, showFavoriteGroups]);

  useEffect(() => {
    if (!open || !showFavoriteGroups || !selectedFavoriteGroupId) {
      if (showFavoriteGroups) setFavoriteRows([]);
      return undefined;
    }

    let alive = true;

    const loadGroupComplexes = async () => {
      setFavoriteLoading(true);
      setFavoriteError('');
      try {
        const response = await getFavoriteGroupComplexes(selectedFavoriteGroupId);
        if (!alive) return;
        setFavoriteRows(Array.isArray(response?.items) ? response.items : []);
      } catch (e) {
        if (!alive) return;
        setFavoriteRows([]);
        setFavoriteError(e?.message || '즐겨찾기 단지 목록을 불러오지 못했습니다.');
      } finally {
        if (alive) setFavoriteLoading(false);
      }
    };

    loadGroupComplexes();
    return () => {
      alive = false;
    };
  }, [open, showFavoriteGroups, selectedFavoriteGroupId]);

  const handleSearch = () => {
    loadPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  if (!open) return null;

  const renderRow = (row, index, prefix = '') => {
    const code = toCode(row?.naverDanjiCode);
    const name = String(row?.buildingName || row?.complexName || code || `단지-${index + 1}`).trim();
    const address = row?.addressRoad || row?.roadAddress || row?.address || row?.jibunAddress
      || [row?.sidoName, row?.sigunguName, row?.bjdName].filter(Boolean).join(' ') || '';
    const familyCnt = row?.familyCnt ?? row?.householdCount ?? row?.households ?? null;
    const useApproveDate = row?.useApproveDate || row?.useApprovalDate || '';
    const ptpNo = String(row?.ptpNo ?? row?.ptp_no ?? '').trim();
    const planTypeName = String(row?.planTypeName ?? row?.plan_type_name ?? '').trim();
    const planTypeName2 = String(row?.planTypeName2 ?? row?.plan_type_name2 ?? '').trim();
    // 평형명: 타입명(예: "34A, 34B")에서 평 추출(추가 표기와 동일). 없으면 공급면적 환산 → 원본명
    const supplyArea = Number(row?.ptpSupplyArea ?? row?.ptp_supply_area);
    const pyeongFromArea = (Number.isFinite(supplyArea) && supplyArea > 0)
      ? `${Math.round(supplyArea / 3.305785)}평` : '';
    const pyeongLabel = parsePyeongLabel(planTypeName2) || parsePyeongLabel(planTypeName)
      || pyeongFromArea || planTypeName2 || planTypeName;
    // 동일 단지라도 평형(ptpNo)이 다르면 별개 대상 → code|ptpNo 로 중복 판정
    const dedupeKey = `${code}|${ptpNo}`;
    const disabled = !code || excludeSet.has(dedupeKey);

    // 연차 = 사용승인 연도 기준
    const ageText = (() => {
      const y = Number(String(useApproveDate || '').slice(0, 4));
      if (!Number.isFinite(y) || y <= 0) return '';
      return `${Math.max(1, new Date().getFullYear() - y + 1)}년차`;
    })();
    const familyText = (familyCnt != null && Number(familyCnt) > 0)
      ? `${Number(familyCnt).toLocaleString('ko-KR')}세대` : '';
    const metaText = [address || '-', familyText, ageText].filter(Boolean).join(' · ');

    return (
      <div key={`${prefix}${dedupeKey}-${index}`} className="ranker-search-popup-item">
        <div className="ranker-search-popup-item-main">
          <div className="ranker-search-popup-item-name">
            {name}
            {pyeongLabel && <span className="ranker-search-popup-item-pyeong">{pyeongLabel}</span>}
          </div>
          <div className="ranker-search-popup-item-meta">{metaText}</div>
        </div>
        <button
          type="button"
          className="ranker-search-popup-add"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            onSelect?.({
              code,
              name,
              address,
              familyCnt,
              useApproveDate,
              ptpNo,
              planTypeName,
              planTypeName2,
            });
          }}
        >
          {disabled ? '추가됨' : '추가'}
        </button>
      </div>
    );
  };

  return (
    <div className="ranker-search-popup-overlay" onClick={onClose}>
      <div className="ranker-search-popup" onClick={(event) => event.stopPropagation()}>
        <header className="ranker-search-popup-header">
          <h4>{title}</h4>
          <button type="button" className="ranker-search-popup-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div className="ranker-search-popup-tabs" role="tablist">
          <button
            type="button"
            className={`ranker-search-popup-tab ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
          >
            검색
          </button>
          {showFavoriteGroups && (
            <button
              type="button"
              className={`ranker-search-popup-tab ${activeTab === 'favorite' ? 'active' : ''}`}
              onClick={() => setActiveTab('favorite')}
            >
              즐겨찾기에서 선택
            </button>
          )}
        </div>

        {activeTab === 'search' && (
          <>
            <div className="ranker-search-popup-search">
              <input
                type="text"
                value={keyword}
                placeholder="단지명으로 검색"
                onChange={(event) => setKeyword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSearch();
                }}
              />
              <button type="button" onClick={handleSearch} disabled={loading}>
                검색
              </button>
            </div>

            {loading && <div className="ranker-search-popup-state">검색 중입니다...</div>}
            {!loading && error && <div className="ranker-search-popup-state error">{error}</div>}
            {!loading && !error && !searched && (
              <div className="ranker-search-popup-state">단지명을 입력해 검색하세요.</div>
            )}
            {!loading && !error && searched && rows.length === 0 && (
              <div className="ranker-search-popup-state">검색 결과가 없습니다.</div>
            )}
            {!loading && !error && rows.length > 0 && (
              <div className="ranker-search-popup-list">
                {rows.map((row, index) => renderRow(row, index))}
              </div>
            )}

            <div className="ranker-search-popup-pagination">
              <button
                type="button"
                onClick={() => loadPage(pageNumber - 1)}
                disabled={loading || pageNumber <= 1}
              >
                이전
              </button>
              <span>{pageNumber} / {totalPages}</span>
              <button
                type="button"
                onClick={() => loadPage(pageNumber + 1)}
                disabled={loading || pageNumber >= totalPages}
              >
                다음
              </button>
            </div>
          </>
        )}

        {activeTab === 'favorite' && showFavoriteGroups && (
          <section className="ranker-search-popup-favorite-section">
            <div className="ranker-search-popup-favorite-groups">
              {favoriteGroups.length === 0 ? (
                <div className="ranker-search-popup-favorite-empty">등록된 즐겨찾기 그룹이 없습니다.</div>
              ) : (
                favoriteGroups.map((group) => {
                  const groupId = Number(group?.groupId || 0);
                  const active = groupId > 0 && groupId === Number(selectedFavoriteGroupId);
                  return (
                    <button
                      key={groupId || String(group?.groupName || '')}
                      type="button"
                      className={`ranker-search-popup-favorite-group-btn ${active ? 'active' : ''}`}
                      onClick={() => setSelectedFavoriteGroupId(groupId)}
                    >
                      {group?.groupName || `그룹 ${groupId}`}
                    </button>
                  );
                })
              )}
            </div>

            {favoriteLoading && <div className="ranker-search-popup-state">즐겨찾기 단지를 불러오는 중입니다...</div>}
            {!favoriteLoading && favoriteError && (
              <div className="ranker-search-popup-state error">{favoriteError}</div>
            )}
            {!favoriteLoading && !favoriteError && favoriteRows.length === 0 && favoriteGroups.length > 0 && (
              <div className="ranker-search-popup-state">이 그룹에 단지가 없습니다.</div>
            )}
            {!favoriteLoading && !favoriteError && favoriteRows.length > 0 && (
              <div className="ranker-search-popup-list favorite">
                {favoriteRows.map((row, index) => renderRow(row, index, 'favorite-'))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default RankerComplexSearchPopup;

